import { useEffect, useRef, useCallback, useState, DependencyList } from 'react';
import { logger } from '../services/logger.client';

/**
 * A safe version of useEffect for async operations that only runs once on mount.
 * Use this instead of useEffect with an empty dependency array when you need to
 * fetch data or perform async initialization.
 *
 * @example
 * useMountEffect(async () => {
 *   const data = await fetchData();
 *   setData(data);
 * });
 */
export function useMountEffect(effect: () => void | Promise<void>): void {
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) {
      logger.warn('useMountEffect: Effect attempted to run multiple times. This might indicate a problem.');
      return;
    }
    hasRunRef.current = true;

    const result = effect();

    // Handle cleanup if the effect returns a promise
    if (result instanceof Promise) {
      result.catch((err) => {
        logger.error({ err }, 'useMountEffect: Async effect threw an error');
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Creates a stable callback reference that won't change between renders.
 * Use this to wrap callbacks that are passed as dependencies to useEffect,
 * useMemo, or useCallback to prevent unnecessary re-renders.
 *
 * The callback will always have access to the latest props and state without
 * needing to be listed in dependency arrays.
 *
 * @example
 * const handleClick = useStableCallback((id: string) => {
 *   // This always has access to latest state
 *   console.log(id, latestState);
 * });
 *
 * useEffect(() => {
 *   // handleClick never changes, so this effect only runs once
 *   document.addEventListener('click', handleClick);
 *   return () => document.removeEventListener('click', handleClick);
 * }, [handleClick]);
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic utility requires 'any' for type inference across all function signatures
export function useStableCallback<T extends (...args: any[]) => any>(
  callback: T
): T {
  const callbackRef = useRef<T>(callback);

  // Update the ref to the latest callback on every render
  useEffect(() => {
    callbackRef.current = callback;
  });

  // Return a stable function that calls the latest callback
  return useCallback(((...args: Parameters<T>) => {
    return callbackRef.current(...args);
  }) as T, []);
}

/**
 * A hook for async effects with proper cleanup and concurrent fetch prevention.
 * This prevents the common pitfall of having state updates in dependencies
 * that get modified inside the effect.
 *
 * @example
 * useAsyncEffect(async (isCancelled) => {
 *   const data = await fetchData();
 *   if (isCancelled()) return; // Don't update state if unmounted
 *   setData(data);
 * }, [someId]); // Only re-run when someId changes
 */
export function useAsyncEffect(
  effect: (isCancelled: () => boolean) => Promise<void>,
  deps: DependencyList
): void {
  const isMountedRef = useRef(true);
  const isRunningRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    // Prevent concurrent executions
    if (isRunningRef.current) {
      logger.warn('useAsyncEffect: Previous effect is still running. Skipping.');
      return;
    }

    isRunningRef.current = true;
    const isCancelled = () => !isMountedRef.current;

    effect(isCancelled)
      .catch((err) => {
        if (!isCancelled()) {
          logger.error({ err }, 'useAsyncEffect: Async effect threw an error');
        }
      })
      .finally(() => {
        isRunningRef.current = false;
      });

    return () => {
      isMountedRef.current = false;
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Detects if a component is re-rendering excessively (potential infinite loop).
 * Only active in development mode. Logs a warning if render count exceeds threshold.
 *
 * @param componentName - Name of the component for debugging
 * @param threshold - Number of renders before warning (default: 50)
 *
 * @example
 * const MyComponent = () => {
 *   useRenderLoopDetection('MyComponent');
 *   // ... rest of component
 * };
 */
export function useRenderLoopDetection(
  componentName: string,
  threshold = 50
): void {
  const renderCountRef = useRef(0);
  const lastResetRef = useRef(Date.now());

  if (import.meta.env.DEV) {
    renderCountRef.current += 1;
    const now = Date.now();

    // Reset counter every second
    if (now - lastResetRef.current > 1000) {
      renderCountRef.current = 1;
      lastResetRef.current = now;
    }

    if (renderCountRef.current > threshold) {
      logger.error(
        { componentName, renderCount: renderCountRef.current },
        'Potential infinite render loop detected'
      );
      logger.debug({ stack: new Error().stack }, 'Component render stack trace');
    }
  }
}

/**
 * A safer alternative to useState for values that should only update
 * when they actually change (deep equality check).
 * Prevents infinite loops caused by creating new object/array references.
 *
 * @example
 * // BAD: Creates new array every render, causes infinite loop if in useEffect deps
 * const [items, setItems] = useState([]);
 *
 * // GOOD: Only updates when array contents actually change
 * const [items, setItems] = useStableState([]);
 */
export function useStableState<T>(
  initialValue: T | (() => T)
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(initialValue);

  const setStableState = useCallback((value: T | ((prev: T) => T)) => {
    setState((prev) => {
      const newValue = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;

      // Deep equality check (you might want to use a library like lodash for this)
      if (JSON.stringify(prev) === JSON.stringify(newValue)) {
        return prev; // Don't update if values are the same
      }

      return newValue;
    });
  }, []);

  return [state, setStableState];
}
